import { Text, TextInput, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  cellRepsRangeInvalid,
  parseRepsToken,
  repsRangeOff,
  type BlockMember,
} from "@heraion/shared";

import type { ComposerStyles } from "./composerStyles";

type Row = BlockMember["setRows"][number];

// The rep range of one set (#336 S3): a labelled line under the set, like
// the time lines (ComposerSetRow), with the low and the high end. The reps
// cell stays the target. No line on an AMRAP or timed set (Q6): a range
// typed before it stays on the cell, and comes back with the set.
export function ComposerSetRange({
  row,
  onUpdateRow,
  styles,
}: {
  row: Row;
  onUpdateRow: (patch: Partial<Row>) => void;
  styles: ComposerStyles;
}) {
  const { t } = useTranslation();
  if (repsRangeOff(row, row)) return null;
  const invalid = cellRepsRangeInvalid(row, row, parseRepsToken(row.reps)?.reps);
  const label = t("program.repsRange", { defaultValue: "Range" });
  const end = (key: "repsMin" | "repsMax", a11y: string) => (
    <TextInput
      style={[styles.cellInput, styles.secondsInput, invalid && styles.cellInputInvalid]}
      value={row[key] ?? ""}
      onChangeText={(v) => onUpdateRow({ [key]: v })}
      keyboardType="number-pad"
      accessibilityLabel={a11y}
    />
  );
  return (
    <View style={styles.rangeBlock}>
      <View style={styles.chipsLine}>
        <Text style={styles.chipsLabel}>{label}</Text>
        <View style={styles.chipsRow}>
          {end("repsMin", t("program.repsRangeMin", { defaultValue: "Fewest reps" }))}
          {/* The two fields name their end: the dash is for the eye only.
              aria-hidden too: react-native-web drops the other two. */}
          <Text
            style={styles.rangeDash}
            accessibilityElementsHidden
            importantForAccessibility="no"
            aria-hidden
          >
            -
          </Text>
          {end("repsMax", t("program.repsRangeMax", { defaultValue: "Most reps" }))}
        </View>
      </View>
      {/* Its own line: beside the fields it wraps into them under font scale. */}
      {invalid ? (
        <Text accessibilityLiveRegion="polite" style={styles.validationText}>
          {t("program.errCode.invalidRepsRange", {
            defaultValue: "Check the rep range: a low and a high end around the target, like 8-12 around 10.",
          })}
        </Text>
      ) : null}
    </View>
  );
}
